import React from "react";
import { Button } from "@/components/ui/button";
import { Apple } from "lucide-react";
import DietGenerator from "../DietGenerator";

const clientMealTypeLabels = { breakfast: "Śniadanie", lunch: "Obiad", dinner: "Kolacja", snack: "Przekąska" };

// The client's "Dieta" tab: AI diet generator plus the plan assigned by a
// trainer. Split out of ClientDashboard, which had grown past 3400 lines.
const DietTab = ({
  dietGen,
  dietKcal,
  setDietKcal,
  genningDiet,
  generateMyDiet,
  savedDiets,
  savingDiet,
  saveMyDiet,
  todayNutrition,
  setNutritionLogOpen,
}) => {
  return (
    <div className="mode-gentle">
      <div className="view-header">
        <h1>Dieta</h1>
      </div>

      {/* AI diet generator (works without a trainer) */}
      <section style={{ padding: "0 20px 20px" }}>
        <div style={{ background: "var(--glass-bg)", border: "1px solid var(--glass-border)", borderRadius: 14, padding: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 10px" }}>🥗 Wygeneruj plan diety</h3>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              Cel (kcal)
              <input type="number" min="1200" max="4000" step="50" value={dietKcal}
                onChange={(e) => setDietKcal(e.target.value)}
                style={{ width: 100, marginLeft: 8, padding: 8, borderRadius: 8, border: "1px solid var(--border)" }} />
            </label>
            <Button onClick={generateMyDiet} disabled={genningDiet} size="sm">
              {genningDiet ? "Generuję..." : "Generuj"}
            </Button>
          </div>
          {dietGen?.days?.[0] && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {dietGen.days[0].meals.map((m, i) => (
                <div key={i} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{m.meal_label}: {m.title}{m.device === "thermomix" ? " 🔧" : ""}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#FF6B6B" }}>{Math.round(m.calories)} kcal</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>B {Math.round(m.protein)}g · W {Math.round(m.carbs)}g · T {Math.round(m.fat)}g</div>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Razem: {Math.round(dietGen.days[0].totals.calories)} kcal</span>
                <Button size="sm" onClick={saveMyDiet} disabled={savingDiet}>{savingDiet ? "Zapisuję..." : "Zapisz dietę"}</Button>
              </div>
            </div>
          )}
          {savedDiets.length > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px solid var(--glass-border)", paddingTop: 12 }}>
              <h4 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-muted)", margin: "0 0 8px" }}>Zapisane diety</h4>
              {savedDiets.slice(0, 5).map((d) => (
                <div key={d.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                  <span>{d.diet_type} · {d.days?.length || 0} dni</span>
                  <span style={{ color: "var(--text-secondary)" }}>{d.target_calories} kcal</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Macro rings — large */}
      <section style={{padding: '0 20px 20px'}}>
        <h3 style={{fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.15em',color:'rgba(255,255,255,0.4)',fontWeight:600,margin:'0 0 12px 0'}}>Dzisiejsze makro</h3>
        <div style={{display:'flex',gap:'10px'}}>
          {[
            {label: "Kalorie", val: todayNutrition.totals.calories, target: todayNutrition.targets?.calories || 2200, unit: "kcal", color: "#FF6B6B"},
            {label: "Bialko", val: todayNutrition.totals.protein, target: todayNutrition.targets?.protein || 120, unit: "g", color: "#4ADE80"},
            {label: "Wegle", val: todayNutrition.totals.carbs, target: todayNutrition.targets?.carbs || 250, unit: "g", color: "#FBBF24"},
            {label: "Tluszcz", val: todayNutrition.totals.fat, target: todayNutrition.targets?.fat || 70, unit: "g", color: "#60A5FA"},
          ].map(m => {
            const pct = Math.min(100, Math.round((m.val / m.target) * 100));
            const r = 24, c = 2 * Math.PI * r;
            return (
              <div key={m.label} style={{flex:1,background:'var(--glass-bg)',border:'1px solid var(--glass-border)',borderRadius:'14px',padding:'14px 6px',display:'flex',flexDirection:'column',alignItems:'center',gap:'6px'}}>
                <svg width="56" height="56" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
                  <circle cx="28" cy="28" r={r} fill="none" stroke={m.color} strokeWidth="3.5"
                    strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c}
                    strokeLinecap="round" transform="rotate(-90 28 28)" style={{transition:'stroke-dashoffset 0.5s ease'}} />
                  <text x="28" y="30" textAnchor="middle" fill="var(--text)" fontSize="12" fontWeight="600">{pct}%</text>
                </svg>
                <span style={{fontSize:'14px',fontWeight:700,color:'var(--text)'}}>{Math.round(m.val)}</span>
                <span style={{fontSize:'10px',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em'}}>{m.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Today's meals */}
      <section style={{padding: '0 20px 20px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
          <h3 style={{fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.15em',color:'rgba(255,255,255,0.4)',fontWeight:600,margin:0}}>Dzisiejsze posilki</h3>
          <Button variant="outline" size="sm" onClick={() => setNutritionLogOpen(true)} style={{fontSize:'12px',padding:'4px 10px'}}>
            + Dodaj posilek
          </Button>
        </div>

        {todayNutrition.logs.length > 0 ? (
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            {todayNutrition.logs.map((log, i) => (
              <div key={i} style={{background:'var(--glass-bg)',border:'1px solid var(--glass-border)',borderRadius:'12px',padding:'14px 16px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
                  <span style={{fontSize:'14px',fontWeight:600}}>{clientMealTypeLabels[log.meal_type] || log.meal_type}</span>
                  <span style={{fontSize:'13px',fontWeight:700,color:'#FF6B6B'}}>{log.items?.reduce((s,it) => s + (it.calories||0), 0)} kcal</span>
                </div>
                <div style={{fontSize:'12px',color:'var(--text-muted)'}}>
                  {log.items?.map(it => it.name).join(', ')}
                </div>
                {log.items?.[0] && (
                  <div style={{display:'flex',gap:'12px',marginTop:'8px',fontSize:'11px',color:'var(--text-secondary)'}}>
                    <span style={{color:'#4ADE80'}}>B: {log.items.reduce((s,it) => s + (it.protein||0), 0)}g</span>
                    <span style={{color:'#FBBF24'}}>W: {log.items.reduce((s,it) => s + (it.carbs||0), 0)}g</span>
                    <span style={{color:'#60A5FA'}}>T: {log.items.reduce((s,it) => s + (it.fat||0), 0)}g</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{textAlign:'center',padding:'32px 16px',color:'var(--text-muted)',fontSize:'14px',background:'var(--glass-bg)',borderRadius:'12px',border:'1px solid var(--glass-border)'}}>
            <Apple size={32} style={{opacity:0.3,marginBottom:'8px'}} />
            <div>Brak zalogowanych posilkow</div>
            <div style={{fontSize:'12px',marginTop:'4px'}}>Kliknij "+ Dodaj posilek" aby zaczac</div>
          </div>
        )}
      </section>
    </div>
  );
};

export default DietTab;
